/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box,
  Switch,
  CardHeader,
  CardContent,
  TextField,
  InputAdornment,
  FormControlLabel,
  Grid,
  Link,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import NoteAlt from '@mui/icons-material/NoteAlt';
import ImportanceSelect from '../../EnumSelects/ImportanceSelect';
import NoteTypeSelect from '../../EnumSelects/NoteTypeSelect';
import { blueGrey } from '../../../utils/brandColors';

export const HorseNoteComponent = ({
  type,
  onChange,
  eventDataDefaultValues,
  showHeader,
  showDescription,
  entityIdentifier,
}) => {
  const [changeNote, setChangeNote] = React.useState(false);

  React.useEffect(() => {
    setChangeNote(false);
  }, [entityIdentifier])

  const eventData = useSelector((state) => state.eventData.eventData);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);

  const getEDNameFromId = (id) => (eventData.find((ed) => ed.id === id) || { name: 'Unknown' }).name

  const noteValue = (eventDataDefaultValues || {})[getEDNameFromId(selectedBarn.barnMetadata.horseNoteEventDataId)];
  const noteType = (eventDataDefaultValues || {})[getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)]
  const noteImportance = (eventDataDefaultValues || {})[getEDNameFromId(selectedBarn.barnMetadata.horseNoteImportanceEventDataId)]

  const getCardContent = () => (
    <CardContent sx={{ pb: '0 !important' }}>
      <Grid container spacing={2}>
        <Grid item>
          <NoteTypeSelect
            value={noteType}
            onChange={(e) => {
              onChange({
                ...eventDataDefaultValues,
                [getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)]: e.target.value,
              });
            }}
          />
        </Grid>
        <Grid item>
          <ImportanceSelect
            value={noteImportance}
            onChange={(e) => {
              onChange({
                ...eventDataDefaultValues,
                [getEDNameFromId(selectedBarn.barnMetadata.horseNoteImportanceEventDataId)]: e.target.value,
              });
            }}
          />
        </Grid>
        <Grid item>
          <TextField
            sx={{ minWidth: 250 }}
            id="horse-note"
            multiline
            maxRows="3"
            size="small"
            label="Note"
            value={noteValue}
            onChange={(e) => {
              onChange({
                ...eventDataDefaultValues,
                [getEDNameFromId(selectedBarn.barnMetadata.horseNoteEventDataId)]: e.target.value,
              });
            }}
            InputProps={{ startAdornment: <InputAdornment position="start"><NoteAlt /></InputAdornment> }}
          />
        </Grid>
      </Grid>
    </CardContent>
  )

  if (type === 'inline') {
    return (
      changeNote
        ? (
          <Box>
            {getCardContent()}
            <Link
              component="button"
              variant="body2"
              color="secondary"
              underline="hover"
              onClick={() => {
                onChange({
                  ...eventDataDefaultValues,
                  [getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)]: null,
                  [getEDNameFromId(selectedBarn.barnMetadata.horseNoteImportanceEventDataId)]: null,
                  [getEDNameFromId(selectedBarn.barnMetadata.horseNoteEventDataId)]: null,
                });
                setChangeNote(false);
              }}
            >
              Remove Horse Note
            </Link>
          </Box>
        )
        : (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {
                noteType && (
                  <Box sx={{ mr: 1 }}>
                    ({noteType}){' '}
                    {noteValue}
                  </Box>
                )
              }
              <Link
                component="button"
                variant="body2"
                color="secondary"
                underline="hover"
                onClick={() => {
                  setChangeNote(true)
                  if (!noteType) {
                    onChange({
                      ...eventDataDefaultValues,
                      [getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)]: 'Note',
                      [getEDNameFromId(selectedBarn.barnMetadata.horseNoteImportanceEventDataId)]: 'Inform',
                      [getEDNameFromId(selectedBarn.barnMetadata.horseNoteEventDataId)]: '',
                    });
                  }
                }}
              >
                {noteType
                  ? 'Modify'
                  : 'Add'} Horse Note
              </Link>
            </Box>
          </Box>
        )
    )
  }

  return (
    <Box>
      {
        showHeader && (
          <CardHeader
            title={(
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                ><NoteAlt color="secondary" sx={{ mr: 1 }} />Post A Horse Note
                </Box>
                <Switch
                  checked={eventDataDefaultValues
                        && eventDataDefaultValues[getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)]}
                  onChange={(e) => {
                    onChange({
                      ...eventDataDefaultValues,
                      [getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)]: e.target.checked
                        ? 'Note'
                        : null,
                      [getEDNameFromId(selectedBarn.barnMetadata.horseNoteImportanceEventDataId)]: e.target.checked
                        ? 'Inform'
                        : null,
                      [getEDNameFromId(selectedBarn.barnMetadata.horseNoteEventDataId)]: e.target.checked
                        ? ''
                        : null,
                    });
                  }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
                {
                  (eventDataDefaultValues
                    && eventDataDefaultValues[getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)]) && (
                      <FormControlLabel
                        sx={{
                          borderLeft: `1px solid ${blueGrey[50]}`,
                          m: 0,
                          pl: 1,
                        }}
                        labelPlacement="start"
                        label={(
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >Client Viewable <Visibility color="primary" sx={{ ml: 1 }} />
                          </Box>
                        )}
                        control={(
                          <Switch
                            checked={eventDataDefaultValues && eventDataDefaultValues[getEDNameFromId(selectedBarn.barnMetadata.horseNoteClientViewableEventDataId)] === 'Yes'}
                            onChange={(e) => {
                              onChange({
                                ...eventDataDefaultValues,
                                [getEDNameFromId(selectedBarn.barnMetadata.horseNoteClientViewableEventDataId)]: e.target.checked
                                  ? 'Yes'
                                  : 'No',
                              });
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        )}
                      />
                  )
                }
              </Box>
            )}
            subheader={showDescription && 'Creates a note on horse when task of this type is marked complete.'}
            sx={{ pb: 0 }}
          />
        )
      }
      {
        (eventDataDefaultValues
          && eventDataDefaultValues[getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)]) && (
            <Box>
              {getCardContent()}
            </Box>
        )
      }
    </Box>
  );
}

HorseNoteComponent.propTypes = {
  onChange: PropTypes.func,
  eventDataDefaultValues: PropTypes.object,
  type: PropTypes.string,
  showHeader: PropTypes.bool,
  showDescription: PropTypes.bool,
  entityIdentifier: PropTypes.any, // used to trigger when the resource changes and we need to reset form
};

HorseNoteComponent.defaultProps = {
  onChange: () => {},
  eventDataDefaultValues: null,
  type: 'card',
  showHeader: false,
  showDescription: false,
  entityIdentifier: '',
};

export default HorseNoteComponent;
